import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import styles from './CopyableId.module.scss';

interface CopyableIdProps {
  /** The id to show. Missing on rows the provider never returned an id for. */
  value?: string | number | null;
  /** Shown when there is no value to copy. */
  emptyText?: string;
  label?: string;
}

/**
 * Grid cell for an id that is worth copying: the value plus a copy button.
 * With no value there is nothing to copy, so only the placeholder renders.
 */
const CopyableId: React.FC<CopyableIdProps> = ({ value, emptyText = '-', label = 'id' }) => {
  const [copied, setCopied] = useState(false);

  const text = value === null || value === undefined ? '' : String(value).trim();

  if (!text) return <>{emptyText}</>;

  const handleCopy = async (e: React.MouseEvent) => {
    // The row itself is clickable — copying must not trigger it.
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard permission denied — the value is on screen to read anyway.
    }
  };

  return (
    <span className={styles.wrapper}>
      <span className={styles.value} title={text}>
        {text}
      </span>
      <button
        type="button"
        className={`${styles.copyBtn} ${copied ? styles.copied : ''}`}
        onClick={handleCopy}
        aria-label={copied ? `Copied ${label}` : `Copy ${label}`}
        title={copied ? 'Copied' : 'Copy'}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </span>
  );
};

export default CopyableId;
